import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/Button';
import { DataTable } from '../../components/DataTable';
import { DatePicker } from '../../components/DatePicker';
import { useApi } from '../../hooks/useApi';
import {
  ApiError,
  getVendingProducts,
  getVendingPurchases,
  type GetVendingPurchasesParams,
} from '../../lib/api';
import { formatLocal, todayLocal } from '../../lib/time';
import { PS, T } from '../../lib/theme';
import { defaultRange, formatTetri, productDisplayName } from '../../lib/vending';
import type { AdminVendingProduct, VendingPurchase } from '../../lib/types';
import { VendingProductImage } from '../../components/VendingProductImage';
import { VendingProductDialog } from './VendingProductDialog';

const PAGE_SIZE = 50;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function VendingPage() {
  const productsLoader = useCallback((signal: AbortSignal) => getVendingProducts(signal), []);
  const { data: products, error: productsError, loading: productsLoading, refetch: refetchProducts } =
    useApi<AdminVendingProduct[]>(productsLoader);
  const [editing, setEditing] = useState<AdminVendingProduct | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [range, setRange] = useState(() => defaultRange(todayLocal()));
  const [purchases, setPurchases] = useState<VendingPurchase[]>([]);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [rangePurchaseAmount, setRangePurchaseAmount] = useState<number | null>(null);
  const [rangePurchaseAmountLoading, setRangePurchaseAmountLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const activeRangeKey = useRef(`${range.from}:${range.to}`);

  useEffect(() => {
    const controller = new AbortController();
    const rangeKey = `${range.from}:${range.to}`;
    activeRangeKey.current = rangeKey;
    setPurchases([]);
    setPurchaseTotal(0);
    setRangePurchaseAmount(null);
    setRangePurchaseAmountLoading(true);
    setPurchaseLoading(true);
    setMoreLoading(false);
    setPurchaseError(null);
    const params: GetVendingPurchasesParams = { ...range, limit: PAGE_SIZE, offset: 0 };
    void getVendingPurchases(params, controller.signal)
      .then((result) => {
        if (controller.signal.aborted || activeRangeKey.current !== rangeKey) return;
        setPurchases(result.purchases);
        setPurchaseTotal(result.total);
        void (async () => {
          let amount = result.purchases.reduce((sum, purchase) => sum + purchase.amount_minor, 0);
          let offset = result.purchases.length;
          while (offset < result.total) {
            const nextPage = await getVendingPurchases({ ...range, limit: PAGE_SIZE, offset }, controller.signal);
            if (controller.signal.aborted || activeRangeKey.current !== rangeKey) return;
            if (nextPage.purchases.length === 0) return;
            amount += nextPage.purchases.reduce((sum, purchase) => sum + purchase.amount_minor, 0);
            offset += nextPage.purchases.length;
          }
          if (!controller.signal.aborted && activeRangeKey.current === rangeKey) {
            setRangePurchaseAmount(amount);
            setRangePurchaseAmountLoading(false);
          }
        })().catch(() => {
          if (!controller.signal.aborted && activeRangeKey.current === rangeKey) {
            setRangePurchaseAmountLoading(false);
          }
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || activeRangeKey.current !== rangeKey) return;
        setPurchaseError(errorMessage(error, 'Could not load purchases.'));
      })
      .finally(() => {
        if (!controller.signal.aborted && activeRangeKey.current === rangeKey) setPurchaseLoading(false);
      });
    return () => controller.abort();
  }, [range]);

  const sortedProducts = useMemo(
    () => [...(products ?? [])].sort((a, b) => a.display_order - b.display_order || a.id.localeCompare(b.id)),
    [products],
  );
  const nextDisplayOrder = useMemo(
    () => sortedProducts.reduce((max, product) => Math.max(max, product.display_order), 0) + 1,
    [sortedProducts],
  );
  const productColumns = useMemo<ColumnDef<AdminVendingProduct, unknown>[]>(() => [
    { header: 'Image', cell: (info) => {
      const product = info.row.original;
      return product.image_url ? <VendingProductImage key={product.image_url} src={product.image_url} name={productDisplayName(product.translations)} /> : '—';
    } },
    { header: 'Name', cell: (info) => productDisplayName(info.row.original.translations) },
    { header: 'Price', cell: (info) => `${formatTetri(info.row.original.price_minor)} ₾` },
    { header: 'Order', accessorKey: 'display_order' },
    { header: 'Status', cell: (info) => info.row.original.is_active ? 'Active' : 'Archived' },
  ], []);

  const purchaseColumns = useMemo<ColumnDef<VendingPurchase, unknown>[]>(() => [
    { header: 'Paid at', cell: (info) => formatLocal(info.row.original.paid_at, 'dd.MM.yyyy HH:mm') },
    { header: 'Product', accessorKey: 'product_name' },
    { header: 'Qty', accessorKey: 'quantity' },
    { header: 'Amount', cell: (info) => `${formatTetri(info.row.original.amount_minor)} ₾` },
    {
      header: 'Buyer',
      cell: (info) => {
        const { user, payer_email: payerEmail } = info.row.original;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {user ? (
              <Link to={`/users/${user.id}`} style={{ color: PS.linkBlue }}>{user.first_name}</Link>
            ) : (
              <span style={{ color: PS.textSec }}>Anonymous</span>
            )}
            {payerEmail && <span style={{ ...T.caption, color: PS.textSec }}>{payerEmail}</span>}
          </div>
        );
      },
    },
    { header: 'Flitt transaction id', cell: (info) => info.row.original.flitt_transaction_id ?? '—' },
  ], []);

  const loadMore = async () => {
    if (moreLoading || purchaseLoading || purchases.length >= purchaseTotal) return;
    const controller = new AbortController();
    const rangeKey = `${range.from}:${range.to}`;
    const offset = purchases.length;
    setMoreLoading(true);
    setPurchaseError(null);
    try {
      const result = await getVendingPurchases({ ...range, limit: PAGE_SIZE, offset }, controller.signal);
      if (activeRangeKey.current !== rangeKey) return;
      setPurchases((current) => [...current, ...result.purchases]);
      setPurchaseTotal(result.total);
    } catch (error) {
      if (activeRangeKey.current === rangeKey) setPurchaseError(errorMessage(error, 'Could not load purchases.'));
    } finally {
      if (activeRangeKey.current === rangeKey) setMoreLoading(false);
    }
  };

  const changeRange = (field: 'from' | 'to', value: string) => {
    setRange((current) => ({ ...current, [field]: value }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <header><h1 style={{ ...T.sectionTitle, margin: 0 }}>Vending</h1></header>

      <section aria-labelledby="vending-products-heading" style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h2 id="vending-products-heading" style={{ ...T.subheading, margin: 0 }}>Products</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>New product</Button>
            <Button variant="secondary" onClick={() => void refetchProducts()}>Refresh</Button>
          </div>
        </div>
        {productsLoading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
        {productsError && <div role="alert" style={errorStyle}>{productsError.message}</div>}
        {!productsLoading && !productsError && (
          <DataTable
            columns={productColumns}
            data={sortedProducts}
            emptyLabel="No products yet"
            onRowClick={(product) => { setEditing(product); setDialogOpen(true); }}
            getRowLabel={(product) => `Edit product ${productDisplayName(product.translations)}`}
          />
        )}
      </section>

      <section aria-labelledby="vending-purchases-heading" style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div>
            <h2 id="vending-purchases-heading" style={{ ...T.subheading, margin: 0 }}>Purchases</h2>
            {!purchaseLoading && !purchaseError && (
              <p style={{ ...T.caption, color: PS.textSec, margin: '4px 0 0' }}>
                {rangePurchaseAmountLoading
                  ? `${purchaseTotal} purchases · Calculating range total…`
                  : rangePurchaseAmount === null
                    ? `${purchaseTotal} purchases · Range total unavailable`
                    : `${purchaseTotal} purchases · ${formatTetri(rangePurchaseAmount)} ₾ in range`}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
            <div style={dateLabelStyle}>
              <span style={T.label}>From</span>
              <DatePicker value={range.from} onChange={(value) => changeRange('from', value)} ariaLabel="From" />
            </div>
            <div style={dateLabelStyle}>
              <span style={T.label}>To</span>
              <DatePicker value={range.to} onChange={(value) => changeRange('to', value)} ariaLabel="To" />
            </div>
            {purchaseError && <span role="alert" style={{ ...T.caption, color: PS.errorText }}>{purchaseError}</span>}
          </div>
        </div>
        {purchaseLoading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
        {!purchaseLoading && !purchaseError && <DataTable columns={purchaseColumns} data={purchases} emptyLabel="No purchases in this range" />}
        {!purchaseLoading && !purchaseError && purchases.length < purchaseTotal && (
          <Button variant="secondary" onClick={() => void loadMore()} disabled={moreLoading}>
            {moreLoading ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </section>

      <VendingProductDialog
        open={dialogOpen}
        product={editing}
        nextDisplayOrder={nextDisplayOrder}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void refetchProducts()}
      />
    </div>
  );
}

const sectionStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 12,
};
const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'start',
  gap: 12,
  flexWrap: 'wrap' as const,
};
const dateLabelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 4,
};
const errorStyle = {
  padding: 12,
  background: PS.errorBg,
  border: `1px solid ${PS.errorBorder}`,
  color: PS.errorText,
  borderRadius: 3,
  ...T.ui,
};
